import Card from '@/models/Card';
import { DateTime } from 'luxon';

export default class GameState {
  constructor() {
    this.turn = 1;
    this.deck = [];
    this.masterCardIDs = [];
    this.masterHeldBackCardID = null;
    this.masterPlayedCardIDs = [];
    this.slaveCardIDs = [];
    this.slaveHeldBackCardID = null;
    this.slavePlayedCardIDs = [];
    this.drawPileIDs = [];
    this.discardPileIDs = [];
    this.isMasterRed = true;
    this.masterCommandPoints = 0;
    this.slaveCommandPoints = 0;
    this.hasGameMaster = false;
  }

  get drawPile() {
    return this.drawPileIDs.map(uuid => this.deck.find(c => c.uuid === uuid));
  }

  get discardPile() {
    return this.discardPileIDs.map(uuid => this.deck.find(c => c.uuid === uuid));
  }

  get masterCards() {
    return this.masterCardIDs.map(uuid => this.deck.find(c => c.uuid === uuid));
  }

  get slaveCards() {
    return this.slaveCardIDs.map(uuid => this.deck.find(c => c.uuid === uuid));
  }

  get masterPlayedCards() {
    return this.masterPlayedCardIDs
      .map(uuid => this.deck.find(c => c.uuid === uuid));
  }

  get slavePlayedCards() {
    return this.slavePlayedCardIDs
      .map(uuid => this.deck.find(c => c.uuid === uuid));
  }

  get clockCardsPlayed() {
    return [...this.discardPileIDs
      .map(uuid => this.deck.find(c => c.uuid === uuid))
      .filter(x => x.isClock)]
      .sort((a, b) => a.playedAt.localeCompare(b.playedAt));
  }

  get masterHeldBackCard() {
    return this.deck.find(c => c.uuid === this.masterHeldBackCardID);
  }

  get slaveHeldBackCard() {
    return this.deck.find(c => c.uuid === this.slaveHeldBackCardID);
  }

  getCardFromID(uuid) {
    return this.deck.find(c => c.uuid === uuid);
  }

  draw(playerCardIDs) {
    if (this.drawPileIDs.length === 0) {
      return;
    }
    const cardDrawnID = this.drawPileIDs.pop();
    const card = this.getCardFromID(cardDrawnID);
    if (card.isClock) {
      card.playedAt = DateTime.utc().toISO();
      this.discardPileIDs.push(cardDrawnID);
    } else {
      playerCardIDs.push(cardDrawnID);
      if (this.drawPileIDs.length === 0) {
        this.drawPileIDs = this.discardPileIDs.sort(() => Math.random() > 0.5 ? 1 : -1);
      }
    }
  }

  remove(card) {
    this.discardPileIDs = this.discardPileIDs.filter(x => x !== card.uuid);
    this.masterPlayedCardIDs = this.masterPlayedCardIDs.filter(x => x !== card.uuid);
    this.slavePlayedCardIDs = this.slavePlayedCardIDs.filter(x => x !== card.uuid);
    this.deck = this.deck.filter(x => x.uuid !== card.uuid);
  }

  drawForMaster() {
    this.draw(this.masterCardIDs);
  }

  drawForSlave() {
    this.draw(this.slaveCardIDs);
  }

  play(card) {
    // // eslint-disable-next-line no-debugger
    // debugger;
    if (this.masterCardIDs.includes(card.uuid)) {
      this.masterCardIDs = this.masterCardIDs.filter(id => id !== card.uuid);
      this.masterPlayedCardIDs.push(card.uuid);
    } else if (this.slaveCardIDs.includes(card.uuid)) {
      this.slaveCardIDs = this.slaveCardIDs.filter(id => id !== card.uuid);
      this.slavePlayedCardIDs.push(card.uuid);
    } else if (this.masterHeldBackCardID === card.uuid) {
      this.masterPlayedCardIDs.push(this.masterHeldBackCardID);
      this.masterHeldBackCardID = null;
    } else if (this.slaveHeldBackCardID === card.uuid) {
      this.slavePlayedCardIDs.push(this.slaveHeldBackCardID);
      this.slaveHeldBackCardID = null;
    }
    this.discardPileIDs.push(card.uuid);
  }

  discard(card) {
    if (this.masterCardIDs.includes(card.uuid)) {
      this.masterCardIDs = this.masterCardIDs.filter(id => id !== card.uuid);
      this.discardPileIDs.push(card.uuid);
    } else if (this.slaveCardIDs.includes(card.uuid)) {
      this.slaveCardIDs = this.slaveCardIDs.filter(id => id !== card.uuid);
      this.discardPileIDs.push(card.uuid);
    }
  }

  holdBack(card) {
    if (this.masterCardIDs.includes(card.uuid)) {
      if (this.masterHeldBackCardID != null) {
        this.discard(card);
      }
      this.masterHeldBackCardID = card.uuid;
      this.masterCardIDs = this.masterCardIDs.filter(id => id !== card.uuid);
    } else {
      if (this.slaveHeldBackCardID != null) {
        this.discard(card);
      }
      this.slaveHeldBackCardID = card.uuid;
      this.slaveCardIDs = this.slaveCardIDs.filter(id => id !== card.uuid);
    }
  }

  shuffle() {
    this.drawPileIDs = [...this.drawPileIDs, ...this.discardPileIDs]
      .sort(() => Math.random() > 0.5 ? 1 : -1);
    this.discardPileIDs = [];
    this.masterPlayedCardIDs = [];
    this.slavePlayedCardIDs = [];
    for (let card of this.deck) {
      card.playedAt = null;
    }
  }

  shuffleHandIntoDeck(forMaster) {
    this.drawPileIDs = [
      ...this.drawPileIDs,
      ...(forMaster ? this.masterCardIDs : this.slaveCardIDs),
    ].sort(() => Math.random() > 0.5 ? 1 : -1);

    if (forMaster) {
      this.masterCardIDs = [];
    } else {
      this.slaveCardIDs = [];
    }
  }

  findGreenClockCardAndShuffleTheDeck() {
    const greenClock = this.drawPile.find(x => x.isClock && x.imageName === 'clock-green');
    if (greenClock) {
      greenClock.playedAt = DateTime.utc().toISO();
      this.drawPileIDs = this.drawPileIDs.filter(uuid => uuid !== greenClock.uuid);
      this.discardPileIDs.push(greenClock.uuid);
      this.drawPileIDs = this.drawPileIDs.sort(() => Math.random() > 0.5 ? 1 : -1);
    }
  }

  returnDiscardedCardToHand(isMaster, card) {
    this.discardPileIDs = this.discardPileIDs.filter(uuid => uuid !== card.uuid);
    card.playedAt = null;
    if (isMaster) {
      this.masterCardIDs.push(card.uuid);
    } else {
      this.slaveCardIDs.push(card.uuid);
    }
    this.masterPlayedCardIDs = this.masterPlayedCardIDs.filter(uuid => uuid !== card.uuid);
    this.slavePlayedCardIDs = this.slavePlayedCardIDs.filter(uuid => uuid !== card.uuid);
  }

  shuffleDiscardedCardInDeck(card) {
    this.discardPileIDs = this.discardPileIDs.filter(uuid => uuid !== card.uuid);
    card.playedAt = null;
    this.drawPileIDs.push(card.uuid);
    this.drawPileIDs = this.drawPileIDs.sort(() => Math.random() > 0.5 ? 1 : -1);
    this.masterPlayedCardIDs = this.masterPlayedCardIDs.filter(uuid => uuid !== card.uuid);
    this.slavePlayedCardIDs = this.slavePlayedCardIDs.filter(uuid => uuid !== card.uuid);
  }

  static fromObject(obj, instance) {
    Object.keys(obj).forEach((key) => {
      instance[key] = obj[key]; // eslint-disable-line
    });
    instance.deck = instance.deck.map(x => new Card(x));
    return instance;
  }
}
